import { ArrowLeftOutlined, CloseOutlined, DownOutlined, ExportOutlined, FilePdfOutlined } from '@ant-design/icons';
import { cityDisplayName, countyDisplayName, isNegativeAmount, subtractMoney } from '@financiar/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Col, Descriptions, Dropdown, Row, Space, Spin, Table, Tag, Tooltip, Typography } from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { getApiError } from '../../api/client';
import type { Firm, InvoiceDetail, InvoiceLine, InvoiceVatBreakdownRow } from '../../api/types';
import { DateText, formatMoney, formatNumber, formatQty, MoneyText } from '../../lib/format';
import { fetchFirma } from '../firme/api';
import { cancelFactura, duplicateFactura, facturaPdfUrl, fetchFactura, stornoFactura } from './api';
import { EfacturaCard } from './EfacturaCard';
import { EfacturaTag } from './EfacturaTag';
import { PaymentsCard } from './PaymentsCard';

const { Title, Text, Paragraph } = Typography;

const PAYMENT_MEANS_LABELS: Record<string, string> = {
  '10': 'Numerar',
  '30': 'Transfer bancar',
  '42': 'Cont bancar',
};

const FREQUENCY_LABELS: Record<string, string> = {
  MONTHLY: 'Lunar',
  QUARTERLY: 'Trimestrial',
  ANNUAL: 'Anual',
};

/** The party block the document header renders, built either from an issued
 * invoice's frozen snapshot or (draft only) from the live rows. */
interface PartyView {
  name: string;
  vatDisplay: string | null;
  cnp: string | null;
  regCom: string | null;
  addressLine: string;
  cityName: string;
  countyCode: string;
  countryCode: string;
  email: string | null;
  phone: string | null;
  capitalSocial: string | null;
  bank: { bankName: string; iban: string } | null;
}

function PartyBlock({ title, party }: { title: string; party: PartyView }) {
  return (
    <Card size="small" title={title} style={{ height: '100%' }}>
      <Text strong>{party.name}</Text>
      <div>{party.vatDisplay ? `CIF: ${party.vatDisplay}` : null}</div>
      {party.cnp ? <div>CNP: {party.cnp}</div> : null}
      {party.regCom ? <div>Reg. Com.: {party.regCom}</div> : null}
      <div>{party.addressLine}</div>
      <div>
        {cityDisplayName(party.countyCode, party.cityName)}, {countyDisplayName(party.countyCode)}, {party.countryCode}
      </div>
      {party.capitalSocial ? <div>Capital social: {formatMoney(party.capitalSocial)}</div> : null}
      {party.email ? <div>{party.email}</div> : null}
      {party.phone ? <div>{party.phone}</div> : null}
      {party.bank ? (
        <div>
          {party.bank.bankName} — <span className="num">{party.bank.iban}</span>
        </div>
      ) : null}
    </Card>
  );
}

/** Issued/cancelled: the frozen `issuer_snapshot`. Draft: the live firm row,
 * because nothing has been frozen yet — the same fallback the PDF renderer
 * makes (`InvoiceTemplateService.liveIdentity`). */
function supplierView(invoice: InvoiceDetail, firm: Firm | null): PartyView {
  const snapshot = invoice.issuerSnapshot;
  if (snapshot) {
    return {
      name: snapshot.name,
      vatDisplay: snapshot.vatDisplay,
      cnp: null,
      regCom: snapshot.regCom,
      addressLine: snapshot.addressLine,
      cityName: snapshot.cityName,
      countyCode: snapshot.countyCode,
      countryCode: snapshot.countryCode,
      email: snapshot.email,
      phone: snapshot.phone,
      capitalSocial: snapshot.capitalSocial,
      bank: snapshot.bank,
    };
  }
  return {
    name: firm?.name ?? '—',
    vatDisplay: firm ? (firm.isVatPayerToday ? `RO${firm.cui}` : firm.cui) : null,
    cnp: null,
    regCom: firm?.regCom ?? null,
    addressLine: firm?.addressLine ?? '',
    cityName: firm?.cityName ?? '',
    countyCode: firm?.countyCode ?? '',
    countryCode: firm?.countryCode ?? 'RO',
    email: firm?.email ?? null,
    phone: firm?.phone ?? null,
    capitalSocial: firm?.capitalSocial ?? null,
    bank: null,
  };
}

function customerView(invoice: InvoiceDetail): PartyView {
  const snapshot = invoice.customerSnapshot;
  if (snapshot) {
    return {
      name: snapshot.name,
      vatDisplay: snapshot.vatDisplay,
      cnp: snapshot.cnp,
      regCom: snapshot.regCom,
      addressLine: snapshot.addressLine,
      cityName: snapshot.cityName,
      countyCode: snapshot.countyCode,
      countryCode: snapshot.countryCode,
      email: snapshot.email,
      phone: snapshot.phone,
      capitalSocial: null,
      bank: null,
    };
  }
  const client = invoice.client;
  return {
    name: client.name,
    vatDisplay: client.cui ? (client.isVatPayer ? `RO${client.cui}` : client.cui) : null,
    cnp: client.cnp,
    regCom: client.regCom,
    addressLine: client.addressLine,
    cityName: client.cityName,
    countyCode: client.countyCode,
    countryCode: client.countryCode,
    email: client.email,
    phone: client.phone,
    capitalSocial: null,
    bank: null,
  };
}

/**
 * Provenance footer for an invoice generated from a subscription — its billing
 * period, cadence, generation date and a link back to the subscription.
 * Renders nothing for a hand-created invoice (`subscription` is null).
 */
function SubscriptionOriginCard({
  invoice,
  onOpenSubscription,
}: {
  invoice: InvoiceDetail;
  onOpenSubscription: (subscriptionId: string) => void;
}) {
  const subscription = invoice.subscription;
  if (!subscription) return null;
  return (
    <Card title="Generată din abonament" style={{ marginTop: 16 }}>
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        Această factură a fost generată automat din abonamentul „{subscription.name}”.
      </Paragraph>
      <Descriptions bordered size="small" column={{ xs: 1, md: 2 }}>
        <Descriptions.Item label="Abonament">
          <Link to={`/abonamente?abonament=${subscription.id}`}>{subscription.name}</Link>
        </Descriptions.Item>
        <Descriptions.Item label="Frecvență">
          {FREQUENCY_LABELS[subscription.frequency] ?? subscription.frequency}
        </Descriptions.Item>
        <Descriptions.Item label="Perioadă facturată">
          <DateText value={invoice.periodStart} /> – <DateText value={invoice.periodEnd} />
        </Descriptions.Item>
        <Descriptions.Item label="Generată la">
          <DateText value={invoice.createdAt} />
        </Descriptions.Item>
        <Descriptions.Item label="Mod emitere">
          {subscription.autoIssue ? <Tag color="blue">Emisă automat</Tag> : <Tag>Ciornă</Tag>}
        </Descriptions.Item>
      </Descriptions>
      <div style={{ marginTop: 16 }}>
        <Button onClick={() => onOpenSubscription(subscription.id)}>Vezi abonamentul</Button>
      </div>
    </Card>
  );
}

function StatusTags({ invoice }: { invoice: InvoiceDetail }) {
  return (
    <Space size={4}>
      {invoice.kind === 'STORNO' ? <Tag color="red">Storno</Tag> : null}
      {invoice.status === 'DRAFT' ? <Tag>Ciornă</Tag> : null}
      {invoice.status === 'CANCELLED' ? <Tag color="default">Anulată</Tag> : null}
      {invoice.status === 'ISSUED' && invoice.paymentStatus === 'PAID' ? <Tag color="green">Încasată</Tag> : null}
      {invoice.status === 'ISSUED' && invoice.paymentStatus === 'PARTIAL' ? (
        <Tag color="orange">Parțial încasată</Tag>
      ) : null}
      {invoice.status === 'ISSUED' && invoice.paymentStatus === 'UNPAID' ? <Tag color="blue">Emisă</Tag> : null}
      {invoice.stornoedBy.length > 0 ? <Tag color="red">Stornată</Tag> : null}
      <EfacturaTag status={invoice.efacturaStatus} message={invoice.efacturaMessage} />
    </Space>
  );
}

/**
 * The immutable invoice — laid out as the DOCUMENT it is (the same blocks in
 * the same order as the PDF), never as a disabled form.
 *
 * Every identity field comes from the invoice's own frozen snapshots, so an
 * invoice issued before the firm moved or renamed itself keeps showing what
 * was true when it was issued; the live firm/client rows are consulted only
 * for a DRAFT, which has no snapshots yet.
 */
export interface FacturaViewProps {
  id: string;
  /** 'page' = the standalone /facturi/:id route; 'panel' = embedded in the
   * list's split view (compact header, a close button, and in-place navigation
   * so cross-references and the storno result stay inside the list). */
  mode?: 'page' | 'panel';
  /** panel only: collapse the detail back to the full-width grid. */
  onClose?: () => void;
  /** panel only: show another invoice in the SAME panel (storno result,
   * stornoed-by / storno-of links) instead of routing away from the list. */
  onOpenInvoice?: (invoiceId: string) => void;
  autoOpenPayment?: boolean;
  onAutoOpenPaymentHandled?: () => void;
}

export function FacturaView({
  id,
  mode = 'page',
  onClose,
  onOpenInvoice,
  autoOpenPayment = false,
  onAutoOpenPaymentHandled,
}: FacturaViewProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();

  // In panel mode we stay in the list and just swap which invoice the panel
  // shows; as a standalone page we route to it.
  const openInvoice = (invoiceId: string) => {
    if (mode === 'panel' && onOpenInvoice) onOpenInvoice(invoiceId);
    else navigate(`/facturi/${invoiceId}`);
  };

  // A cross-reference to another invoice (storno / storno-of): an in-place
  // panel swap when embedded, a real route link on the standalone page.
  const invoiceRef = (invoiceId: string, label: ReactNode) =>
    mode === 'panel' ? (
      <Typography.Link onClick={() => openInvoice(invoiceId)}>{label}</Typography.Link>
    ) : (
      <Link to={`/facturi/${invoiceId}`}>{label}</Link>
    );

  const { data: invoice, isLoading } = useQuery({
    queryKey: ['invoices', 'detail', id],
    queryFn: () => fetchFactura(id as string),
    enabled: !!id,
  });

  /**
   * An issued/cancelled invoice renders entirely from its own snapshots, so
   * the live firm row is needed ONLY for a draft — and then it has to be the
   * firm DETAIL for this invoice's own company, not a lookup in the
   * FirmaCurenta list (active firms, first page only): a draft belonging to a
   * firm missing from that list would resolve to "neplătitor" and drop the
   * VAT columns off a document that will be issued with VAT.
   */
  const needsLiveFirm = !!invoice && !invoice.issuerSnapshot;
  const { data: firmDetail, isLoading: isLoadingFirm } = useQuery({
    queryKey: ['firms', 'detail', invoice?.companyId],
    queryFn: () => fetchFirma(invoice?.companyId as string),
    enabled: needsLiveFirm && !!invoice?.companyId,
  });

  function reportError(error: unknown, fallback: string) {
    message.error(getApiError(error)?.message ?? fallback);
  }

  const stornoMutation = useMutation({
    mutationFn: stornoFactura,
    onSuccess: (storno) => {
      message.success(`Factura de stornare ${storno.fullNumber ?? ''} a fost emisă.`);
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      openInvoice(storno.id);
    },
    onError: (error) => reportError(error, 'Factura nu a putut fi stornată.'),
  });

  const cancelMutation = useMutation({
    mutationFn: cancelFactura,
    onSuccess: (updated) => {
      message.success('Factura a fost anulată.');
      queryClient.setQueryData(['invoices', 'detail', updated.id], updated);
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error) => reportError(error, 'Factura nu a putut fi anulată.'),
  });

  const duplicateMutation = useMutation({
    mutationFn: duplicateFactura,
    onSuccess: (draft) => {
      message.success('Ciorna a fost creată prin duplicare.');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate(`/facturi/${draft.id}/editare`);
    },
    onError: (error) => reportError(error, 'Factura nu a putut fi duplicată.'),
  });

  // The firm query is part of the gate for a draft: rendering it before the
  // firm's VAT status is known would show a VAT payer's draft without its VAT
  // columns for a frame.
  if (isLoading || !invoice || (needsLiveFirm && isLoadingFirm)) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  const firm = firmDetail ?? null;
  const supplier = supplierView(invoice, firm);
  const customer = customerView(invoice);
  // Whether the ISSUER was a VAT payer for THIS document: frozen on the
  // snapshot for an issued invoice, "as of today" for a draft.
  const showVat = invoice.issuerSnapshot ? invoice.issuerSnapshot.isVatPayer : (firm?.isVatPayerToday ?? false);

  const isIssued = invoice.status === 'ISSUED';
  const canCancel = isIssued && invoice.payments.length === 0;
  const rest = subtractMoney(invoice.totalAmount, invoice.paidAmount);

  const lineColumns: ColumnsType<InvoiceLine> = [
    { title: '#', dataIndex: 'position', width: 44 },
    { title: 'Denumire', dataIndex: 'description' },
    { title: 'UM', dataIndex: 'unitLabel', width: 80 },
    {
      title: 'Cant.',
      key: 'quantity',
      width: 90,
      align: 'right',
      render: (_, row) => <span className="num">{formatQty(row.quantity)}</span>,
    },
    {
      title: 'Preț unitar',
      key: 'unitPrice',
      width: 130,
      align: 'right',
      render: (_, row) => <MoneyText value={row.unitPrice} currency={invoice.currency} />,
    },
    {
      title: 'Valoare',
      key: 'lineNet',
      width: 130,
      align: 'right',
      render: (_, row) => (
        <MoneyText
          value={row.lineNet}
          currency={invoice.currency}
          className={isNegativeAmount(row.lineNet) ? 'money-negative' : undefined}
        />
      ),
    },
    ...(showVat
      ? ([
          {
            title: 'Cotă TVA',
            key: 'vatRatePercent',
            width: 90,
            align: 'right',
            render: (_, row: InvoiceLine) => <span className="num">{formatNumber(row.vatRatePercent)}%</span>,
          },
          {
            title: 'TVA',
            key: 'lineVat',
            width: 120,
            align: 'right',
            render: (_, row: InvoiceLine) => (
              <MoneyText
                value={row.lineVat}
                currency={invoice.currency}
                className={isNegativeAmount(row.lineVat) ? 'money-negative' : undefined}
              />
            ),
          },
        ] as ColumnsType<InvoiceLine>)
      : []),
    {
      title: 'Total linie',
      key: 'lineTotal',
      width: 140,
      align: 'right',
      render: (_, row) => (
        <MoneyText
          value={row.lineTotal}
          currency={invoice.currency}
          className={isNegativeAmount(row.lineTotal) ? 'money-negative' : undefined}
        />
      ),
    },
  ];

  const breakdownColumns: ColumnsType<InvoiceVatBreakdownRow> = [
    { title: 'Categorie', dataIndex: 'vatCategoryCode', width: 100 },
    {
      title: 'Cotă',
      key: 'vatRatePercent',
      width: 90,
      align: 'right',
      render: (_, row) => <span className="num">{formatNumber(row.vatRatePercent)}%</span>,
    },
    {
      title: 'Bază impozabilă',
      key: 'taxableAmount',
      align: 'right',
      render: (_, row) => <MoneyText value={row.taxableAmount} currency={invoice.currency} />,
    },
    {
      title: 'TVA',
      key: 'vatAmount',
      align: 'right',
      render: (_, row) => <MoneyText value={row.vatAmount} currency={invoice.currency} />,
    },
  ];

  const stateActions: MenuProps['items'] = [
    ...(invoice.kind !== 'STORNO' ? [{ key: 'duplicate', label: 'Duplică' }] : []),
    ...(canCancel ? [{ key: 'cancel', label: 'Anulează', danger: true }] : []),
  ];
  // e-Factura actions live in their own card below (EfacturaCard), not here.
  const moreItems: MenuProps['items'] = stateActions;

  function handleMore(key: string) {
    if (!invoice) return;
    if (key === 'duplicate') {
      duplicateMutation.mutate(invoice.id);
      return;
    }
    if (key === 'cancel') {
      modal.confirm({
        title: 'Anulezi factura?',
        content:
          'Numărul rămâne alocat definitiv acestei facturi (o serie nu poate avea goluri), dar factura nu mai reprezintă o creanță. Operațiunea nu poate fi anulată.',
        okText: 'Anulează factura',
        cancelText: 'Renunță',
        okButtonProps: { danger: true },
        onOk: () => cancelMutation.mutateAsync(invoice.id),
      });
    }
  }

  const documentTitle = invoice.kind === 'STORNO' ? 'Factură storno' : 'Factură';
  // Same header line the PDF prints (InvoiceTemplateService): the series
  // prefix and the zero-padded number, not the concatenated fullNumber.
  const headerLine =
    invoice.series && invoice.number !== null
      ? `Seria ${invoice.series.prefix} nr. ${String(invoice.number).padStart(invoice.series.padding, '0')}`
      : 'Ciornă — fără număr';

  return (
    <div>
      <Space wrap style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', rowGap: 8 }} align="start">
        <Space align="center" wrap>
          {mode === 'panel' ? (
            <Tooltip title="Închide">
              <Button icon={<CloseOutlined />} onClick={() => onClose?.()} />
            </Tooltip>
          ) : (
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/facturi')}>
              Facturi
            </Button>
          )}
          <Title level={mode === 'panel' ? 4 : 3} style={{ margin: 0 }}>
            {documentTitle}
          </Title>
          <StatusTags invoice={invoice} />
        </Space>
        <Space wrap>
          {mode === 'panel' ? (
            <Tooltip title="Deschide în pagină">
              <Button icon={<ExportOutlined />} onClick={() => navigate(`/facturi/${invoice.id}`)} />
            </Tooltip>
          ) : null}
          {invoice.status === 'DRAFT' ? (
            <Button onClick={() => navigate(`/facturi/${invoice.id}/editare`)}>Editează ciorna</Button>
          ) : null}
          <Button
            type="primary"
            icon={<FilePdfOutlined />}
            href={facturaPdfUrl(invoice.id)}
            target="_blank"
            rel="noreferrer"
          >
            Descarcă PDF
          </Button>
          {isIssued ? (
            <Button
              danger
              loading={stornoMutation.isPending}
              onClick={() =>
                modal.confirm({
                  title: 'Stornezi factura?',
                  content: `Se emite o factură nouă, de stornare, cu valorile lui ${invoice.fullNumber ?? 'acest document'} inversate și cu număr propriu. Factura originală rămâne pe evidență, neschimbată — stornarea este singura corecție legală a unei facturi emise.`,
                  okText: 'Stornează',
                  cancelText: 'Renunță',
                  onOk: () => stornoMutation.mutateAsync(invoice.id),
                })
              }
            >
              Stornează
            </Button>
          ) : null}
          {moreItems && moreItems.length > 0 ? (
            <Dropdown menu={{ items: moreItems, onClick: ({ key }) => handleMore(key) }} trigger={['click']}>
              <Button>
                Mai multe <DownOutlined />
              </Button>
            </Dropdown>
          ) : null}
        </Space>
      </Space>

      {invoice.stornoedBy.length > 0 ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <span>
              Stornată prin{' '}
              {invoice.stornoedBy.map((storno, index) => (
                <span key={storno.id}>
                  {index > 0 ? ', ' : ''}
                  {invoiceRef(storno.id, storno.fullNumber ?? 'factura de stornare')}
                </span>
              ))}
              .
            </span>
          }
        />
      ) : null}

      {invoice.kind === 'STORNO' && invoice.stornoOf ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <span>
              Stornare la factura {invoiceRef(invoice.stornoOf.id, invoice.stornoOf.fullNumber ?? 'originală')}.
            </span>
          }
        />
      ) : null}

      {invoice.status === 'CANCELLED' ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Factură anulată"
          description="Numărul rămâne alocat acestei facturi, dar documentul nu mai reprezintă o creanță."
        />
      ) : null}

      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} md={12} style={{ marginBottom: 16 }}>
            <PartyBlock title="Furnizor" party={supplier} />
          </Col>
          <Col xs={24} md={12} style={{ marginBottom: 16 }}>
            <PartyBlock title="Cumpărător" party={customer} />
          </Col>
        </Row>

        <Descriptions bordered size="small" column={{ xs: 1, md: 3 }} style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Document">
            <span className="num">{headerLine}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Dată emitere">
            <DateText value={invoice.issueDate} />
          </Descriptions.Item>
          <Descriptions.Item label="Scadență">
            <DateText value={invoice.dueDate} />
          </Descriptions.Item>
          <Descriptions.Item label="Dată livrare">
            <DateText value={invoice.supplyDate} />
          </Descriptions.Item>
          <Descriptions.Item label="Mod plată">
            {PAYMENT_MEANS_LABELS[invoice.paymentMeansCode] ?? invoice.paymentMeansCode}
          </Descriptions.Item>
          <Descriptions.Item label="Monedă">{invoice.currency}</Descriptions.Item>
        </Descriptions>

        <Table<InvoiceLine>
          rowKey="id"
          size="small"
          pagination={false}
          columns={lineColumns}
          dataSource={invoice.lines}
          scroll={{ x: showVat ? 1000 : 760 }}
        />

        <Row gutter={16} style={{ marginTop: 16 }} justify="end">
          {showVat && invoice.vatBreakdown.length > 0 ? (
            <Col xs={24} md={12}>
              <Text type="secondary">Defalcare TVA</Text>
              <Table<InvoiceVatBreakdownRow>
                rowKey="id"
                size="small"
                pagination={false}
                columns={breakdownColumns}
                dataSource={invoice.vatBreakdown}
                style={{ marginTop: 8 }}
              />
            </Col>
          ) : null}
          <Col xs={24} md={showVat && invoice.vatBreakdown.length > 0 ? 12 : 10}>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Subtotal">
                <MoneyText
                  value={invoice.subtotalAmount}
                  currency={invoice.currency}
                  className={isNegativeAmount(invoice.subtotalAmount) ? 'money-negative' : undefined}
                />
              </Descriptions.Item>
              {showVat ? (
                <Descriptions.Item label="TVA">
                  <MoneyText
                    value={invoice.vatAmount}
                    currency={invoice.currency}
                    className={isNegativeAmount(invoice.vatAmount) ? 'money-negative' : undefined}
                  />
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label={<Text strong>Total</Text>}>
                <Text strong>
                  <MoneyText
                    value={invoice.totalAmount}
                    currency={invoice.currency}
                    className={isNegativeAmount(invoice.totalAmount) ? 'money-negative' : undefined}
                  />
                </Text>
              </Descriptions.Item>
              {isIssued ? (
                <Descriptions.Item label="Încasat">
                  <MoneyText value={invoice.paidAmount} currency={invoice.currency} />
                </Descriptions.Item>
              ) : null}
              {isIssued ? (
                <Descriptions.Item label="Rest de încasat">
                  <MoneyText
                    value={rest}
                    currency={invoice.currency}
                    className={isNegativeAmount(rest) ? 'money-negative' : undefined}
                  />
                </Descriptions.Item>
              ) : null}
            </Descriptions>
          </Col>
        </Row>

        {!showVat ? (
          <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
            Neplătitor de TVA
          </Paragraph>
        ) : null}

        {invoice.notes ? (
          <>
            <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
              Note
            </Text>
            <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{invoice.notes}</Paragraph>
          </>
        ) : null}
      </Card>

      {isIssued ? (
        <PaymentsCard invoice={invoice} autoOpen={autoOpenPayment} onAutoOpenHandled={onAutoOpenPaymentHandled} />
      ) : null}

      {isIssued && invoice.kind !== 'PROFORMA' ? <EfacturaCard invoice={invoice} /> : null}

      <SubscriptionOriginCard
        invoice={invoice}
        onOpenSubscription={(subscriptionId) => navigate(`/abonamente?abonament=${subscriptionId}`)}
      />
    </div>
  );
}

/**
 * The standalone `/facturi/:id` route: reads the invoice id from the URL and
 * the one-shot `?incasare=1` flag that auto-opens the payment dialog, then
 * renders the very same `FacturaView` the list's split-view panel embeds.
 */
export function FacturaViewPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [autoOpenPayment, setAutoOpenPayment] = useState(searchParams.get('incasare') === '1');

  if (!id) return null;

  return (
    <FacturaView
      id={id}
      mode="page"
      autoOpenPayment={autoOpenPayment}
      onAutoOpenPaymentHandled={() => {
        setAutoOpenPayment(false);
        searchParams.delete('incasare');
        setSearchParams(searchParams, { replace: true });
      }}
    />
  );
}
